import {
  PERMISSIONS,
  hasPermission,
  useSession,
} from "@/modules/auth/useSession";
import {
  DRIVER_DOCUMENT_KINDS,
  DRIVER_DOCUMENT_NOTE_MAX,
  type DriverDocumentKind,
  type RosterDocumentState,
  type RosterDocumentView,
} from "@/lib/api/dispatchTypes";

/**
 * The drivers roster and document review (driver backend B5 · P18, plan
 * T11–T15). Pure helpers plus the one access hook the pages share.
 */

export const driverRosterQueryKeys = {
  all: ["restaurant", "drivers"] as const,
  list: ["restaurant", "drivers", "list"] as const,
  details: (sellerId: string) =>
    ["restaurant", "drivers", "details", sellerId] as const,
  views: (sellerId: string) =>
    ["restaurant", "drivers", "views", sellerId] as const,
};

/** The search parameter that opens a driver's drawer on the Drivers list. */
export const DRIVER_PARAM = "driver";

/** The Drivers list with `sellerId`'s drawer open — what a link should point at. */
export function driverDrawerHref(locale: string, sellerId: string): string {
  return `/${locale}/manage/staff/drivers?${DRIVER_PARAM}=${encodeURIComponent(sellerId)}`;
}

/**
 * Who may see what, mirroring the endpoints' own grants:
 * - the roster and a driver's profile/vehicle (T11, T12): `DISPATCH_MANAGE`
 *   (cashier, manager, owner) OR `DRIVER_DOCUMENTS_VIEW`;
 * - documents, opening one (logged), reviewing, the access log (T12's
 *   document list, T13–T15): `DRIVER_DOCUMENTS_VIEW` only (manager, owner).
 * Orientation, not security — the server refuses anything else.
 */
export function useDriverRosterAccess() {
  const { data: session } = useSession();
  const canViewDocuments = hasPermission(
    session,
    PERMISSIONS.DRIVER_DOCUMENTS_VIEW,
  );
  const canSeeRoster =
    canViewDocuments || hasPermission(session, PERMISSIONS.DISPATCH_MANAGE);
  return { canSeeRoster, canViewDocuments };
}

/** A kind's state on the till: its document's status, or MISSING. */
export type DocumentReviewState = RosterDocumentState["status"] | "MISSING";

export type DocumentStateCounts = {
  accepted: number;
  waiting: number;
  resubmit: number;
  missing: number;
};

/** The roster's per-driver document summary over the eight kinds. */
export function documentStateCounts(
  documents: readonly RosterDocumentState[],
): DocumentStateCounts {
  const byKind = new Map(documents.map((doc) => [doc.kind, doc.status]));
  const counts: DocumentStateCounts = {
    accepted: 0,
    waiting: 0,
    resubmit: 0,
    missing: 0,
  };
  for (const kind of DRIVER_DOCUMENT_KINDS) {
    const status = byKind.get(kind);
    if (status === "ACCEPTED") counts.accepted += 1;
    else if (status === "RESUBMIT") counts.resubmit += 1;
    else if (status === "SUBMITTED") counts.waiting += 1;
    else counts.missing += 1;
  }
  return counts;
}

/** One row per kind, in review order, with its current document (if any). */
export function documentRows(
  documents: readonly RosterDocumentView[],
): { kind: DriverDocumentKind; document: RosterDocumentView | null }[] {
  const byKind = new Map(documents.map((doc) => [doc.kind, doc]));
  return DRIVER_DOCUMENT_KINDS.map((kind) => ({
    kind,
    document: byKind.get(kind) ?? null,
  }));
}

/**
 * The resubmit note as the server will store it: trimmed, 1–500 characters.
 * `null` = not sendable (empty or too long).
 */
export function resubmitNote(raw: string): string | null {
  const note = raw.trim();
  return note.length >= 1 && note.length <= DRIVER_DOCUMENT_NOTE_MAX
    ? note
    : null;
}

/**
 * Opens a document's short-lived URL in a new tab.
 *
 * The tab is opened SYNCHRONOUSLY, inside the click, before the URL exists:
 * a `window.open` after an `await` is a pop-up the browser blocks. The URL is
 * then fetched (T13 writes the access-log row first) and assigned to that tab
 * — it is never put in state, a query cache, the DOM or a log, and a second
 * click asks the server again. The server pins the type (images inline, PDFs
 * as a download). Returns `"blocked"` when the browser refused the tab.
 */
export async function openDocumentInNewTab(
  fetchUrl: () => Promise<{ url: string }>,
  open: (url: string, target: string) => Window | null = (url, target) =>
    window.open(url, target),
): Promise<"opened" | "blocked"> {
  const tab = open("about:blank", "_blank");
  if (!tab) return "blocked";
  try {
    // The new tab must not be able to reach back into the till.
    tab.opener = null;
    const { url } = await fetchUrl();
    tab.location.href = url;
    return "opened";
  } catch (error) {
    tab.close();
    throw error;
  }
}
